import React, { FC, SyntheticEvent, useMemo } from 'react';
import Badge from '@atlaskit/badge';
import Lozenge from '@atlaskit/lozenge';
import WorldIcon from '@atlaskit/icon/glyph/world';
import ArrowUpCircleIcon from '@atlaskit/icon/glyph/arrow-up-circle';
import { token } from '@atlaskit/tokens';
import { SloDTO } from '@atlassiansox/phobos-client-ts';
import Pagination from '@atlaskit/pagination';

import {
  flexStart,
  spaceGapCompact,
  spaceTopCozy,
  textTransformCapitalize,
  colors,
  marginPushRight,
} from '@/styles';
import { KeyValue } from '@/components/primitive';
import GoalIcon from '@/components/primitive/goal-icon';

import { badgeStyles, generalBarStyles } from '../styles';
import { formatCohortName } from '../utils';

import { cohortTitleStyles, iconSizeAlignStyles } from './styles';
import SLOCohortMembers from './slo-cohort-members';

type SLOCohortProps = {
  isRollup: boolean;
  slos: SloDTO[];
  cohort: string;
  isDraft: boolean;
  showPagination?: boolean;
  pages?: number[];
  currentPage?: number;
  onPageChange?: (e: SyntheticEvent, newPage: number) => void;
};

const SLOCohort: FC<SLOCohortProps> = ({
  isRollup,
  slos,
  cohort,
  isDraft,
  showPagination,
  pages = [],
  currentPage = 1,
  onPageChange = () => {},
}) => {
  const cohortIcon = useMemo(() => {
    if (isRollup)
      switch (cohort.toLowerCase()) {
        case 'global':
          return (
            <span css={iconSizeAlignStyles}>
              <WorldIcon
                primaryColor={token('color.text.subtlest', colors.N100)}
                label=""
                size="small"
              />
            </span>
          );
        default:
          return (
            <ArrowUpCircleIcon
              primaryColor={token('color.text.subtlest', colors.N100)}
              label=""
              size="small"
            />
          );
      }
    else {
      return (
        <span css={iconSizeAlignStyles}>
          <GoalIcon />
        </span>
      );
    }
  }, [cohort, isRollup]);

  const cohortName: string = useMemo(() => formatCohortName(cohort), [cohort]);

  return (
    <div>
      <div css={[flexStart, spaceGapCompact, generalBarStyles, cohortTitleStyles]}>
        {cohortIcon}
        <Lozenge appearance={isRollup ? 'inprogress' : 'default'} isBold>
          {isRollup ? 'rollup' : 'base'}
        </Lozenge>
        <KeyValue noPositionFix title="cohort">
          <Badge>
            <span css={[badgeStyles, textTransformCapitalize]}>{cohortName}</span>
          </Badge>
        </KeyValue>
        {isDraft && <Lozenge appearance="moved">draft</Lozenge>}
        {showPagination && (
          <div css={marginPushRight}>
            <Pagination selectedIndex={currentPage - 1} pages={pages} onChange={onPageChange} />
          </div>
        )}
      </div>
      <div css={spaceTopCozy}>
        <SLOCohortMembers slos={slos} />
      </div>
    </div>
  );
};

export default SLOCohort;
